export function createMirror(image, ctx, mockData = {}) {
  // Validate inputs
  if (!image || !ctx || typeof ctx.save !== 'function') {
    console.warn('createMirror: Invalid parameters');
    return;
  }
  
  // Extract options from mockData and image.mirrorShadow
  const shadowData = image.mirrorShadow && image.mirrorShadow[0] ? image.mirrorShadow[0] : {};
  const elementId = image.element_id || 'default';
  
  const options = {
    opacity: shadowData.opacity,
    fade: shadowData.fade,
    offsetY: shadowData.offsetY || 0,
    offsetX: shadowData.offsetX || 0,
    direction: mockData[`${elementId}_mirror_position`] || 'bottom'
  };
  
  // Save the current context state
  ctx.save();
  
  // Get image dimensions
  const imgWidth = image.width;
  const imgHeight = image.height;
    
  // Get the image element
  let imageElement;
  if (image._element) {
    imageElement = image._element;
  } else if (image.getElement) {
    imageElement = image.getElement();
  } else {
    console.warn('No image element found');
    ctx.restore();
    return;
  }
  
  // Create mirror effect
  if (options.direction && imageElement) {
    // Create a temporary canvas for the mirror effect
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = imgWidth;
    tempCanvas.height = imgHeight;
    const tempCtx = tempCanvas.getContext('2d');
        
    // Draw the mirrored image on the temporary canvas
    tempCtx.save();
    
    // Apply the correct transformations based on direction
    switch (options.direction) {
      case 'bottom':
        // Flip vertically for bottom mirror
        tempCtx.scale(1, -1);
        tempCtx.translate(0, -imgHeight);
        break;
      case 'top':
        // Flip vertically for top mirror
        tempCtx.scale(1, -1);
        tempCtx.translate(0, -imgHeight);
        break;
      case 'left':
        // Flip horizontally for left mirror
        tempCtx.scale(-1, 1);
        tempCtx.translate(-imgWidth, 0);
        break;
      case 'right':
        // Flip horizontally for right mirror
        tempCtx.scale(-1, 1);
        tempCtx.translate(-imgWidth, 0);
        break;
    }
    
    // Draw the original image with transformations applied
    tempCtx.drawImage(imageElement, 0, 0, imgWidth, imgHeight);
    tempCtx.restore();
    
    // Apply fade gradient effect
    if (options.fade >= 0) {
      tempCtx.save();
      tempCtx.globalCompositeOperation = 'destination-in';
      
      let gradient;
      const fadePercent = Math.min(100, Math.max(0, options.fade)) / 100;
      
      switch (options.direction) {
        case 'bottom':
          // Fade from top to bottom for bottom mirror
          gradient = tempCtx.createLinearGradient(0, 0, 0, imgHeight);
          gradient.addColorStop(0, `rgba(255, 255, 255, ${options.opacity})`);
          gradient.addColorStop(fadePercent, `rgba(255, 255, 255, ${options.opacity * 0.1})`);
          gradient.addColorStop(Math.min((fadePercent + 0.01), 1), `rgba(255, 255, 255, 0)`);
          gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
          break;
        case 'top':
          // Fade from bottom to top for top mirror
          gradient = tempCtx.createLinearGradient(0, imgHeight, 0, 0);
          gradient.addColorStop(0, `rgba(255, 255, 255, ${options.opacity})`);
          gradient.addColorStop(fadePercent, `rgba(255, 255, 255, ${options.opacity * 0.1})`);
          gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
          break;
        case 'left':
          // Fade from right to left for left mirror
          gradient = tempCtx.createLinearGradient(imgWidth, 0, 0, 0);
          gradient.addColorStop(0, `rgba(255, 255, 255, ${options.opacity})`);
          gradient.addColorStop(fadePercent, `rgba(255, 255, 255, ${options.opacity * 0.1})`);
          gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
          break;
        case 'right':
          // Fade from left to right for right mirror
          gradient = tempCtx.createLinearGradient(0, 0, imgWidth, 0);
          gradient.addColorStop(0, `rgba(255, 255, 255, ${options.opacity})`);
          gradient.addColorStop(fadePercent, `rgba(255, 255, 255, ${options.opacity * 0.1})`);
          gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
          break;
      }
      
      tempCtx.fillStyle = gradient;
      tempCtx.fillRect(0, 0, imgWidth, imgHeight);
      tempCtx.restore();
    }

    // Calculate position for the mirror on the main canvas
    let mirrorX = 0, mirrorY = 0;
    
    switch (options.direction) {
      case 'bottom':
        mirrorX = -(imgWidth/2);
        mirrorY = (imgHeight/2) + options.offsetY / image.scaleY
        break;
      case 'top':
        mirrorX = -(imgWidth/2);
        mirrorY = -(imgHeight * 1.5) + options.offsetY / image.scaleY;
        break;
      case 'left':
        mirrorX = -imgWidth * 1.5 - options.offsetX / image.scaleX;
        mirrorY = -imgHeight * 0.5;
        break;
      case 'right':
        mirrorX = (imgHeight * 0.5) + options.offsetX / image.scaleX;
        mirrorY = -imgHeight * 0.5;
        break;
    }
    
    // Draw the processed mirror on the main canvas
    ctx.save();
    // Don't apply additional opacity here if it's already been applied in the gradient
    if (options.fade === 0) {
      ctx.globalAlpha = options.opacity;
    }
    ctx.drawImage(tempCanvas, mirrorX, mirrorY, imgWidth, imgHeight);
    ctx.restore();
  }
   
  // Restore the original context state
  ctx.restore();
}
